/**
 * Fellido · Sticky-Kaufleiste
 *
 * Erscheint, sobald der echte Warenkorb-Button aus dem Bild scrollt.
 * Kauft NICHT selbst: der Leisten-Button klickt den echten Button an.
 * Damit laufen Horizons Formularlogik und die Geschenk-Auswahl unveraendert
 * weiter — keine zweite Quelle der Wahrheit.
 */
class FStickyBar extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-f-sb-bar]');
    this.btn = this.querySelector('[data-f-sb-btn]');
    this.priceEl = this.querySelector('[data-f-sb-price]');
    this.compareEl = this.querySelector('[data-f-sb-compare]');
    this.saveEl = this.querySelector('[data-f-sb-save]');
    this.lowestEl = this.querySelector('[data-f-sb-lowest]');
    this.imgEl = this.querySelector('[data-f-sb-img]');
    this.variantEl = this.querySelector('[data-f-sb-variant]');

    try {
      this.variants = JSON.parse(this.querySelector('[data-f-sb-variants]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }

    this.sichtbar = false;
    this.imEditor = Boolean(window.Shopify?.designMode);

    this.btn?.addEventListener('click', this.onClick);
    document.addEventListener('change', this.onChange);
    document.addEventListener('keydown', this.onKey);

    this.beobachteZiel();
    this.sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.scroller?.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('change', this.onChange);
    document.removeEventListener('keydown', this.onKey);
  }

  /** Der echte Warenkorb-Button — nie der aus dieser Leiste. */
  echterButton() {
    const alle = [...document.querySelectorAll('.add-to-cart-button, [data-f-atc]')];
    return alle.find((b) => !this.contains(b) && !b.closest('sticky-add-to-cart')) || null;
  }

  /**
   * Horizon scrollt nicht das Fenster, sondern .page-wrapper.
   * Ein Beobachter ohne root wuerde in manchen Faellen nichts melden —
   * deshalb den echten Scroll-Container suchen und als root setzen.
   */
  scrollContainer(von) {
    let n = von?.parentElement;
    while (n && n !== document.body) {
      const overflow = getComputedStyle(n).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') {
        if (n.scrollHeight > n.clientHeight) return n;
      }
      n = n.parentElement;
    }
    return null;
  }

  beobachteZiel() {
    const art = this.dataset.trigger || 'button';

    if (art === 'immediate') {
      this.zeige(true);
      return;
    }

    if (art === 'percent') {
      const anteil = Math.min(Math.max(parseInt(this.dataset.triggerPercent, 10) || 15, 1), 95) / 100;
      // Denselben Anker wie im Button-Modus nehmen: von dort aus findet
      // scrollContainer den echten Scroll-Bereich. Von document.body aus
      // waere die Suche nach oben immer ergebnislos.
      const root = this.scrollContainer(this.echterButton() || document.body);
      this.scroller = root || window;
      this.onScroll = () => {
        if (this.tick) return;
        this.tick = requestAnimationFrame(() => {
          this.tick = null;
          const el = root || document.documentElement;
          const hoehe = el.scrollHeight - el.clientHeight;
          const stand = root ? root.scrollTop : window.scrollY;
          this.zeige(hoehe > 0 && stand / hoehe >= anteil);
        });
      };
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      return;
    }

    const ziel = this.echterButton();
    // Ohne Kaufbereich waere die Leiste sinnlos — dann bleibt sie weg.
    if (!ziel) return;
    this.ziel = ziel;

    const root = this.scrollContainer(ziel);

    this.observer = new IntersectionObserver(
      ([eintrag]) => {
        // Sichtbar, sobald der echte Button oben aus dem Bild gescrollt ist.
        const drueber = eintrag.boundingClientRect.top < 0;
        this.zeige(!eintrag.isIntersecting && drueber);
      },
      { root, rootMargin: '0px 0px -8px 0px', threshold: 0 }
    );
    this.observer.observe(ziel);

    // Rueckfallebene: der Beobachter allein reagiert nicht in jeder Umgebung
    // (etwa im Theme-Editor). Ein gedrosselter Scroll-Horcher haelt den
    // Zustand auch dann richtig.
    this.scroller = root || window;
    this.onScroll = () => {
      if (this.tick) return;
      this.tick = requestAnimationFrame(() => {
        this.tick = null;
        this.pruefeStand();
      });
    };
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
  }

  pruefeStand() {
    if (!this.ziel) return;
    const r = this.ziel.getBoundingClientRect();
    // Oberkante des Buttons ueber dem Sichtfeld = Leiste zeigen.
    this.zeige(r.bottom < 0);
  }

  zeige(an) {
    if (an === this.sichtbar) return;
    this.sichtbar = an;
    this.dataset.state = an ? 'shown' : 'hidden';
    this.bar?.setAttribute('aria-hidden', an ? 'false' : 'true');
  }

  onKey = (event) => {
    // Waehrend der Warenkorb offen ist, hat die Leiste nichts zu suchen.
    if (event.key === 'Escape') this.pruefeWarenkorb();
  };

  pruefeWarenkorb() {
    const cart = document.querySelector('f-cart');
    this.dataset.cartOpen = cart?.dataset.state === 'open' ? 'true' : 'false';
  }

  onClick = (event) => {
    event.preventDefault();
    const ziel = this.echterButton();
    if (!ziel) return;
    this.pruefeWarenkorb();
    ziel.click();
  };

  onChange = () => {
    requestAnimationFrame(() => this.sync());
  };

  aktuelleVariante() {
    const input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (!input) return null;
    return this.variants.find((v) => v.id === Number(input.value)) || null;
  }

  sync() {
    const v = this.aktuelleVariante();
    if (!v) return;

    if (this.priceEl) this.priceEl.textContent = this.geld(v.price);

    const reduziert = v.compare > v.price;

    if (this.compareEl) {
      this.compareEl.hidden = !reduziert;
      if (reduziert) this.compareEl.textContent = this.geld(v.compare);
    }

    if (this.saveEl) {
      this.saveEl.hidden = !reduziert;
      if (reduziert) {
        const pct = Math.round(((v.compare - v.price) * 100) / v.compare);
        this.saveEl.textContent = this.saveEl.dataset.tpl.replace('{pct}', pct).replace(
          '{amount}',
          this.geld(v.compare - v.price)
        );
      }
    }

    // Pflichtangabe nach PAngV nur zeigen, wenn tatsaechlich reduziert wird.
    if (this.lowestEl) this.lowestEl.hidden = !reduziert || !this.lowestEl.dataset.filled;

    if (this.variantEl) {
      const zeigen = Boolean(v.title) && v.title !== 'Default Title';
      this.variantEl.hidden = !zeigen;
      if (zeigen) this.variantEl.textContent = v.title;
    }

    if (this.imgEl && v.image) this.imgEl.src = v.image;

    if (this.btn) {
      this.btn.disabled = !v.available;
      const label = this.btn.querySelector('[data-f-sb-label]');
      if (label) {
        label.textContent = v.available ? this.btn.dataset.labelIn : this.btn.dataset.labelOut;
      }
    }
  }

  geld(cents) {
    return `${(cents / 100).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} €`;
  }
}

if (!customElements.get('f-sticky-bar')) {
  if (!customElements.get('f-sticky-bar')) customElements.define('f-sticky-bar', FStickyBar);
}
